import ReceiptIcon from '@mui/icons-material/Receipt';
import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import {
  ActionButton,
  BoxFlex,
  BrowserOnly,
  CopyQRCode,
  QrCodeShowButton,
} from '@woovi/ui';

import type { PaymentLinkActiveRoot_charge$key } from './__generated__/PaymentLinkActiveRoot_charge.graphql.ts';
import PaymentLinkActiveCashbackFidelityBalance from './PaymentLinkActiveCashbackFidelityBalance.tsx';
import PaymentLinkActiveEarnCashback from './PaymentLinkActiveEarnCashback.tsx';
import PaymentLinkActiveEarnCashbackFidelity from './PaymentLinkActiveEarnCashbackFidelity.tsx';
import PaymentLinkActiveEarnDiscount from './PaymentLinkActiveEarnDiscount.tsx';
import PaymentLinkActiveInterestsSettings from './PaymentLinkActiveInterestsSettings.tsx';
import PaymentLinkActiveOpenBankInfo from './PaymentLinkActiveOpenBankInfo.tsx';
import PaymentLinkActivePixCreditCard from './PaymentLinkActivePixCreditCard.tsx';
import PaymentLinkActiveQrCode from './PaymentLinkActiveQrCode.tsx';
import { useChargePaymentMethod } from '../../../charge/useChargePaymentMethod.tsx';
import PaymentInstallmentPlan from '../../installment/PaymentInstallmentPlan.tsx';
import ITPButton from '../../itp/ITPButton.tsx';
import PaymentLinkAdditionalInfo from '../../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkPaymentTerm from '../../paymentLink/PaymentLinkPaymentTerm.tsx';
import PaymentLinkShareButton from '../../paymentLink/PaymentLinkShareButton.tsx';
import PaymentLinkTransactionDetails from '../../PaymentLinkTransactionDetails.tsx';
import PaymentActivePixCobValue from '../pixCob/PaymentActivePixCobValue.tsx';
import ChargePrintPDFButton from './pdf/ChargePrintPDF.tsx';

type PaymentLinkActiveProps = {
  charge: PaymentLinkActiveRoot_charge$key;
  alwaysShowQrCode?: boolean;
  allowSimulatePayment?: boolean;
};

const PaymentLinkActiveRoot = ({
  allowSimulatePayment = false,
  ...props
}: PaymentLinkActiveProps): ReactNode => {
  const { t } = useTranslation();

  const [qrCodeShow, setQrCodeShow] = useState(false);
  const [copyQRCodeError, setCopyQRCodeError] = useState(false);

  const charge = useFragment<PaymentLinkActiveRoot_charge$key>(
    graphql`
      fragment PaymentLinkActiveRoot_charge on Charge {
        id
        brCode
        identifier
        company {
          nameFriendly
          logo {
            url(fitIn: true)
          }
          projectType
          taxID {
            taxID
          }
        }
        paymentMethods {
          ... on PaymentMethodPix {
            brCode
          }
        }
        ...PaymentActivePixCobValue_charge
        ...PaymentLinkComment_charge
        ...PaymentLinkActiveEarnDiscount_charge
        ...PaymentLinkActiveEarnCashback_charge
        ...PaymentLinkActiveCashbackFidelityBalance_charge
        ...PaymentLinkActiveEarnCashbackFidelity_charge
        ...PaymentLinkActiveQrCode_charge
        ...PaymentLinkShareButton_charge
        ...PaymentLinkActiveInterestsSettings_charge
        ...PaymentInstallmentPlan_charge
        ...PaymentLinkPaymentTerm_charge
        ...PaymentLinkTransactionDetails_charge
        ...PaymentLinkAdditionalInfo_charge
        ...useChargePaymentMethod_charge
        ...PaymentLinkActivePixCreditCard_charge
        ...ITPButton_charge
        ...ChargePrintPDF_charge
      }
    `,
    props.charge,
  );

  const { pixCob, pix } = useChargePaymentMethod(charge);

  const brCode = pixCob?.brCode || pix?.brCode;

  const renderShowQrCode = (): ReactNode => {
    if (!props.alwaysShowQrCode) {
      return (
        <QrCodeShowButton
          qrCodeShow={qrCodeShow}
          setQrCodeShow={setQrCodeShow}
        />
      );
    }

    return null;
  };

  return (
    <BoxFlex
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        mt: '10px',
      }}
    >
      <BoxFlex
        sx={{
          justifyContent: 'center',
          flexDirection: 'column',
          display: 'flex',
          fontSize: '20px',
          fontWeight: 'bold',
        }}
      >
        <PaymentActivePixCobValue charge={charge} />
        <PaymentLinkComment charge={charge} />
      </BoxFlex>

      <BoxFlex
        sx={{
          flexDirection: 'column',
        }}
      >
        <BoxFlex sx={{ flexDirection: 'column', alignItems: 'center' }}>
          <BoxFlex sx={{ flexDirection: 'column', gap: 1, my: 1 }}>
            <PaymentLinkActiveEarnDiscount charge={charge} />
            <PaymentLinkActiveEarnCashback charge={charge} />
            <PaymentLinkActiveCashbackFidelityBalance charge={charge} />
            <PaymentLinkActiveEarnCashbackFidelity charge={charge} />
          </BoxFlex>

          <BoxFlex
            sx={{
              flexDirection: 'column',
              gap: 1,
              maxWidth: '332px',
              width: '100%',
            }}
          >
            <PaymentLinkActiveQrCode
              charge={charge}
              qrCodeShow={qrCodeShow}
              setQrCodeShow={setQrCodeShow}
              copyQRCodeError={copyQRCodeError}
              alwaysShowQrCode={props.alwaysShowQrCode}
            />
            <PaymentLinkActiveOpenBankInfo />
            <CopyQRCode
              value={brCode}
              onError={(error) => setCopyQRCodeError(error)}
            />
            <ITPButton charge={charge} />
            <PaymentLinkShareButton charge={charge} pixQrCode={null} />
            <BrowserOnly>
              <ChargePrintPDFButton
                charge={charge}
                trigger={({ disabled, onPrint }) => (
                  <ActionButton
                    disabled={disabled}
                    onClick={onPrint}
                    variant='outlined'
                    color='tertiary'
                    size='large'
                    startIcon={
                      <ReceiptIcon color={disabled ? 'disabled' : 'tertiary'} />
                    }
                  >
                    {t('DOWNLOAD PIX INVOICE')}
                  </ActionButton>
                )}
              />
            </BrowserOnly>
            {renderShowQrCode()}
          </BoxFlex>
          <PaymentLinkActiveInterestsSettings charge={charge} />
          {/* @todo: use Stack */}
          <BoxFlex sx={{ marginTop: '16px' }}>
            <PaymentInstallmentPlan charge={charge} />
          </BoxFlex>
          <PaymentLinkPaymentTerm charge={charge} />
        </BoxFlex>
        <PaymentLinkActivePixCreditCard charge={charge} />
        <PaymentLinkTransactionDetails charge={charge} />
        <PaymentLinkAdditionalInfo charge={charge} />
      </BoxFlex>
    </BoxFlex>
  );
};

export default PaymentLinkActiveRoot;
